import {
  memo, useEffect, useRef, useState,
} from '../../lib/teact/teact';

import buildClassName from '../../util/buildClassName';

import useLastCallback from '../../hooks/useLastCallback';

import Icon from '../common/icons/Icon';

import styles from './UploadKnowledgeModal.module.scss';

export type ProgramType = 'diplomado' | 'maestria' | 'especialidad' | 'institucional';
export type TabType = 'file' | 'markdown';

export interface NewKnowledgeBaseItem {
  id: number;
  program_code: string;
  title: string;
  program_type: string;
  status: string;
  total_chunks: number;
  source_file?: string;
}

type OwnProps = {
  isOpen: boolean;
  onClose: NoneToVoidFunction;
  onSuccess: (item: NewKnowledgeBaseItem) => void;
  initialFile?: File;
};

import { CRM_BACKEND_URL as BACKEND_URL } from '../../util/crmBackendUrl';

const SUGGESTION_CHIPS = [
  'DFTMV1',
  'EFAPTV1',
  'DFGREAV1-2026',
  'DNDRFV1',
  'FISIO-2026',
  'SALUD-PUB-2026',
  'INSTITUCIONAL-2026',
];

const PROGRAM_TYPES: { id: ProgramType; label: string; icon: string }[] = [
  { id: 'diplomado', label: '🎓 Diplomado', icon: 'study' },
  { id: 'maestria', label: '🏛️ Maestría', icon: 'channel' },
  { id: 'especialidad', label: '🩺 Especialidad', icon: 'user' },
  { id: 'institucional', label: '📋 Información General / FAQs', icon: 'document' },
];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

const MARKDOWN_PLACEHOLDER = [
  'Pega aquí el contenido estructurado del programa en formato Markdown...',
  '',
  '# Diplomado en...',
  '• Duración: 6 meses',
  '• Modalidad: Virtual sincrónica (asistencia 100% obligatoria)',
  '• Arancel: 3500 Bs.',
].join('\n');

const UploadKnowledgeModal = ({
  isOpen,
  onClose,
  onSuccess,
  initialFile,
}: OwnProps) => {
  const [programType, setProgramType] = useState<ProgramType>('diplomado');
  const [isClosed, setIsClosed] = useState(false);
  const [programCode, setProgramCode] = useState('');
  const [title, setTitle] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>('file');
  const [selectedFile, setSelectedFile] = useState<File | undefined>(initialFile);
  const [contentText, setContentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [isDragOver, setIsDragOver] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>();

  useEffect(() => {
    if (isOpen) {
      setProgramType('diplomado');
      setIsClosed(false);
      setProgramCode('');
      setTitle(initialFile ? initialFile.name.replace(/\.[^/.]+$/, '') : '');
      setActiveTab('file');
      setSelectedFile(initialFile);
      setContentText('');
      setIsSubmitting(false);
      setErrorMessage(undefined);
      setIsDragOver(false);
    }
  }, [isOpen, initialFile]);

  const handleToggleClosed = useLastCallback(() => {
    const nextClosed = !isClosed;
    setIsClosed(nextClosed);

    if (nextClosed) {
      if (programCode && !programCode.toUpperCase().startsWith('CERRADO')) {
        setProgramCode(`CERRADO${programCode}`);
      }
      if (title && !title.includes('(CERRADO)')) {
        setTitle(`${title} (CERRADO)`);
      }
    } else {
      if (programCode.toUpperCase().startsWith('CERRADO')) {
        setProgramCode(programCode.replace(/^CERRADO/i, ''));
      }
      if (title.includes('(CERRADO)')) {
        setTitle(title.replace(/\s*\(CERRADO\)/i, ''));
      }
    }
  });

  const handleSelectChip = useLastCallback((chip: string) => {
    const finalCode = isClosed && !chip.startsWith('CERRADO') ? `CERRADO${chip}` : chip;
    setProgramCode(finalCode);
  });

  const handleFileChange = useLastCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (!title) {
        setTitle(file.name.replace(/\.[^/.]+$/, ''));
      }
      setErrorMessage(undefined);
    }
  });

  const handleDragEnter = useLastCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
    setIsDragOver(true);
  });

  const handleDragOver = useLastCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
    setIsDragOver(true);
  });

  const handleDragLeave = useLastCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const relatedTarget = e.relatedTarget as Node | null;
    if (e.currentTarget.contains(relatedTarget)) {
      return;
    }
    setIsDragOver(false);
  });

  const handleDrop = useLastCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    let file: File | undefined = e.dataTransfer.files?.[0];
    if (!file && e.dataTransfer.items?.length) {
      for (let i = 0; i < e.dataTransfer.items.length; i++) {
        const item = e.dataTransfer.items[i];
        if (item.kind === 'file') {
          file = item.getAsFile() || undefined;
          if (file) break;
        }
      }
    }

    if (file) {
      setSelectedFile(file);
      if (!title) {
        setTitle(file.name.replace(/\.[^/.]+$/, ''));
      }
      setErrorMessage(undefined);
    }
  });

  const handleSubmit = useLastCallback(async () => {
    const cleanCode = programCode.trim().toUpperCase();
    const cleanTitle = title.trim();

    if (!cleanCode) {
      setErrorMessage('Por favor ingresa o selecciona un código de programa (ej. DFTMV1).');
      return;
    }

    if (!cleanTitle) {
      setErrorMessage('Por favor especifica el nombre o título oficial del programa.');
      return;
    }

    if (activeTab === 'file' && !selectedFile) {
      setErrorMessage('Por favor adjunta un archivo (.docx, .pdf, .txt o .md).');
      return;
    }

    if (activeTab === 'markdown' && !contentText.trim()) {
      setErrorMessage('Por favor ingresa o pega el contenido Markdown del documento.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(undefined);

    const formData = new FormData();
    formData.append('program_code', cleanCode);
    formData.append('title', cleanTitle);
    formData.append('program_type', programType);
    formData.append('status', isClosed ? 'closed' : 'indexed');

    if (activeTab === 'file' && selectedFile) {
      formData.append('file', selectedFile);
    } else {
      formData.append('content_text', contentText.trim());
    }

    try {
      const res = await fetch(`${BACKEND_URL}/knowledge-bases`, {
        method: 'POST',
        body: formData,
      });

      if (res.ok) {
        const data = await res.json();
        if (data && data.item) {
          onSuccess(data.item);
        } else {
          onSuccess({
            id: Date.now(),
            program_code: cleanCode,
            title: cleanTitle,
            program_type: programType,
            status: isClosed ? 'closed' : 'indexed',
            total_chunks: data?.chunks_indexed || 1,
            source_file: selectedFile?.name || `${cleanCode.toLowerCase()}.md`,
          });
        }
        onClose();
      } else {
        const errData = await res.json().catch(() => ({}));
        let errorMsg = 'Error al procesar e indexar el documento.';
        if (typeof errData.detail === 'string') {
          errorMsg = errData.detail;
        } else if (Array.isArray(errData.detail) && errData.detail.length > 0) {
          errorMsg = errData.detail.map((d: any) => d.msg || JSON.stringify(d)).join(', ');
        }
        setErrorMessage(errorMsg);
      }
    } catch {
      setErrorMessage('Error de red al conectar con el servidor de indexación.');
    } finally {
      setIsSubmitting(false);
    }
  });

  if (!isOpen) return undefined;

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerInfo}>
            <div className={styles.headerIcon}>
              <Icon name="document" />
            </div>
            <div>
              <h3 className={styles.headerTitle}>Subir e Indexar Documento RAG</h3>
              <p className={styles.headerSubtitle}>
                Segmentación semántica y generación de vectores en Qdrant con BAAI/bge-m3
              </p>
            </div>
          </div>
          <button
            type="button"
            className={styles.btnClose}
            onClick={onClose}
            aria-label="Cerrar modal"
          >
            <Icon name="close" />
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {/* Selector de Tipo y Estado */}
          <div className={styles.row}>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                Nivel Académico / Tipo
                <span className={styles.required}>*</span>
              </label>
              <select
                className={styles.select}
                value={programType}
                onChange={(e) => setProgramType(e.target.value as ProgramType)}
              >
                {PROGRAM_TYPES.map((pt) => (
                  <option key={pt.id} value={pt.id}>
                    {pt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Checkbox / Toggle Estado (solo para programas formativos) */}
            {programType !== 'institucional' ? (
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Estado de la Convocatoria</label>
                <div className={styles.statusCard}>
                  <div className={styles.statusInfo}>
                    <span className={styles.statusTitle}>
                      {isClosed ? '🔴 Convocatoria Cerrada' : '🟢 Convocatoria Abierta'}
                    </span>
                    <span className={styles.statusDesc}>
                      {isClosed
                        ? 'Inició clases; IA calculará meses a Enero'
                        : 'Inscripciones vigentes; IA ofrece cupos'}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={buildClassName(
                      styles.statusToggle,
                      isClosed ? styles.closedStatus : styles.activeStatus,
                    )}
                    onClick={handleToggleClosed}
                  >
                    {isClosed ? 'Cerrado' : 'Abierto'}
                  </button>
                </div>
              </div>
            ) : (
              <div className={styles.fieldGroup}>
                <label className={styles.label}>Clasificación</label>
                <div className={styles.statusCard}>
                  <div className={styles.statusInfo}>
                    <span className={styles.statusTitle}>🏛️ Institucional</span>
                    <span className={styles.statusDesc}>Información general transversal para el RAG</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Código de Programa con Chips */}
          <div className={styles.fieldGroup}>
            <label className={styles.label}>
              Código de Programa Académico
              <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              className={styles.input}
              placeholder="Ej. DFTMV1, EFAPTV1, FISIO-2026..."
              value={programCode}
              onChange={(e) => setProgramCode(e.target.value)}
            />
            <div className={styles.chipsRow}>
              {SUGGESTION_CHIPS.map((chip) => (
                <button
                  key={chip}
                  type="button"
                  className={styles.chip}
                  onClick={() => handleSelectChip(chip)}
                >
                  {isClosed && !chip.startsWith('CERRADO') ? `CERRADO${chip}` : chip}
                </button>
              ))}
            </div>
          </div>

          {/* Nombre Oficial del Programa */}
          <div className={styles.fieldGroup}>
            <label className={styles.label}>
              Nombre / Título Oficial del Programa
              <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              className={styles.input}
              placeholder="Ej. Diplomado en Farmacología y Terapéutica Médica"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Pestañas de Método de Carga */}
          <div className={styles.tabsHeader}>
            <button
              type="button"
              className={buildClassName(styles.tabButton, activeTab === 'file' && styles.activeTab)}
              onClick={() => setActiveTab('file')}
            >
              <Icon name="document" />
              <span>Adjuntar Archivo (.docx, .pdf, .txt, .md)</span>
            </button>
            <button
              type="button"
              className={buildClassName(styles.tabButton, activeTab === 'markdown' && styles.activeTab)}
              onClick={() => setActiveTab('markdown')}
            >
              <Icon name="edit" />
              <span>Escribir / Pegar Markdown</span>
            </button>
          </div>

          {/* Contenido de Carga: Archivo */}
          {activeTab === 'file' && (
            <div>
              {selectedFile ? (
                <div className={styles.filePreviewCard}>
                  <div className={styles.fileInfo}>
                    <div className={styles.fileIcon}>
                      <Icon name="document" />
                    </div>
                    <div>
                      <span className={styles.fileName}>{selectedFile.name}</span>
                      <div className={styles.fileSize}>{formatFileSize(selectedFile.size)}</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={styles.btnRemoveFile}
                    onClick={() => {
                      setSelectedFile(undefined);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                  >
                    Cambiar archivo
                  </button>
                </div>
              ) : (
                <div
                  className={buildClassName(styles.dropzone, isDragOver && styles.dragOver)}
                  onClick={() => fileInputRef.current?.click()}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  data-dropzone="true"
                  role="button"
                  tabIndex={0}
                >
                  <div className={styles.dropzoneIcon}>
                    <Icon name="attach" />
                  </div>
                  <span className={styles.dropzoneTitle}>
                    Haz clic para buscar o arrastra y suelta tu archivo aquí
                  </span>
                  <span className={styles.dropzoneSubtitle}>
                    Soporta documentos Word (.docx), PDF (.pdf), Texto (.txt) o Markdown (.md) hasta 10 MB
                  </span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".docx,.pdf,.txt,.md"
                    className={styles.hiddenInput}
                    onChange={handleFileChange}
                  />
                </div>
              )}
            </div>
          )}

          {/* Contenido de Carga: Pegar Markdown */}
          {activeTab === 'markdown' && (
            <div className={styles.fieldGroup}>
              <textarea
                className={styles.textarea}
                placeholder={MARKDOWN_PLACEHOLDER}
                value={contentText}
                onChange={(e) => setContentText(e.target.value)}
              />
            </div>
          )}

          {errorMessage && (
            <div className={styles.errorMessage}>
              ⚠️
              {' '}
              {errorMessage}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.btnCancel}
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={styles.btnSubmit}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            <Icon name="check" />
            <span>{isSubmitting ? 'Procesando e Indexando...' : 'Procesar e Indexar'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default memo(UploadKnowledgeModal);
